import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Box,
    Badge,
    Avatar,
    Menu,
    MenuItem,
    ListItemIcon,
    Dialog,
    CardHeader,
    CardActions,
    Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate, useLocation } from 'react-router-dom';
import { logout, setUser } from '../../Store/authSlice';
import NotificationsPopup from '../common/NotificationsPopup';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { setActiveStep, setFiles, setFormData, setNotiFications } from '../../Store/userSlice';
import { toast } from 'react-toastify';

function LogoutDialog({ open, onClose, onConfirm }) {
    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <CardHeader
                sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    textAlign: 'center'
                }}
                title="Are you sure you want to logout?"
            />
            <CardActions sx={{ 
                justifyContent: 'center',
                margin: '16px'
            }}>
                <Button 
                    onClick={onClose} 
                    variant="contained" 
                    color="inherit"
                >
                    Cancel
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    color="primary"
                >
                    Yes
                </Button>
            </CardActions>
        </Dialog>
    );
}

const Header = ({ drawerWidth, handleDrawerToggle }) => {
    const user = useSelector((state) => state.auth.user);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [anchorEl, setAnchorEl] = useState(null);
    const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
    const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
    const open = Boolean(anchorEl);
    const notificationsOpen = Boolean(notificationsAnchorEl);
    const previousNotification = useSelector((state) => state?.user?.notifications)
    

    useEffect(() => {
        const socket = new SockJS('https://api-v1.dequitec.com/ws');
        const client = new Client({
          webSocketFactory: () => socket,
          onConnect: () => {
            client.subscribe(`/topic/notifications/${String(user?.role).toLowerCase==="superadmin"?"admin":"manager"}`, (message) => {
              if (message.body) {
                const notification = JSON.parse(message.body);
                // Add new notification and update localStorage
                const previousNotifications = previousNotification || [];
                const notificationWithId = {
                  ...notification,
                  id: notification.id || `notification-${Date.now()}`,
                  unread: true
                };
                const updatedNotifications = [notificationWithId, ...previousNotifications];
                dispatch(setNotiFications(updatedNotifications));
                // localStorage.setItem('notifications', JSON.stringify(updatedNotifications)); // Store in localStorage
              }
            });
          },
          onStompError: (error) => {
            console.error('STOMP error:', error);
          },
        });
    
        client.activate();
        return () => client.deactivate();
      }, [user, previousNotification]); 

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleNavigation = (target) => {
        if (location.pathname === '/apply-loan') {
            const event = new CustomEvent('beforeNavigate', { detail: { target } });
            window.dispatchEvent(event);
        } else {
            navigate(target);
        }
        handleClose();
    };

    const handleProfile = () => handleNavigation('/profile');
    const handleChangePassword = () => handleNavigation('/change-password');

    const handleLogoutClick = () => {
        handleClose();
        setLogoutDialogOpen(true);
    };

    const handleLogoutCancel = () => {
        setLogoutDialogOpen(false);
    };

    const handleLogoutConfirm = async () => {
        const proceedLogout = async () => {
            try {
                await dispatch(logout()).unwrap();
            } catch (error) {
                toast.error(error.message);
            } finally {
                dispatch(setUser({}));
                dispatch(setFormData({}));
                dispatch(setFiles({}));
                dispatch(setActiveStep(0));
                window.localStorage.clear();
                window.sessionStorage.clear();
                navigate('/login');
            }
        };

        if (location.pathname === '/apply-loan') {
            const event = new CustomEvent('beforeLogout', { cancelable: true });
            const handled = event.defaultPrevented || !window.dispatchEvent(event);

            if (!handled) {
                await proceedLogout();
            } else {
                const onDraftSaved = () => {
                    window.removeEventListener('draftSaved', onDraftSaved);
                    proceedLogout();
                };
                window.addEventListener('draftSaved', onDraftSaved, { once: true });
            }
        } else {
            await proceedLogout();
        }
        setLogoutDialogOpen(false);
    };
    const handleNotificationsClick = (event) => {
        setNotificationsAnchorEl(event.currentTarget);
    };
    const handleNotificationsClose = () => {
        setNotificationsAnchorEl(null);
    };

    

    return (
        <AppBar
            position="fixed"
            sx={{
                width: { sm: `calc(100% - ${drawerWidth}px)` },
                ml: { sm: `${drawerWidth}px` },
                bgcolor: 'white',
                color: 'black',
                boxShadow: 'none',
                borderBottom: '1px solid #e0e0e0',
                borderRadius: 0,  
                
            }}
        >
            <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { sm: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box 
                        sx={{ 
                            position: 'relative',
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            overflow: 'hidden'
                        }}
                    >
                        <Box 
                            sx={{ 
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '33%',
                                bgcolor: '#FF9933' // Saffron
                            }} 
                        />
                        <Box 
                            sx={{ 
                                position: 'absolute',
                                top: '33%',
                                left: 0,
                                width: '100%',
                                height: '34%',
                                bgcolor: '#FFFFFF', // White
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }} 
                        >
                            <Box 
                                sx={{ 
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: '#000080', // Navy blue for Ashoka Chakra
                                }} 
                            />
                        </Box>
                        <Box 
                            sx={{ 
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                width: '100%',
                                height: '33%',
                                bgcolor: '#138808' // Green
                            }} 
                        />
                    </Box>
                    <IconButton 
                            onClick={handleNotificationsClick}
                            sx={{ padding: 1 }}
                        >
                            <Badge 
                                badgeContent={previousNotification?.filter(notification => notification.unread)?.length || 0} 
                                color="error"
                                invisible={!previousNotification?.filter(notification => notification.unread)?.length}
                                sx={{ 
                                    '& .MuiBadge-badge': { 
                                        backgroundColor: '#FF0000',
                                        color: 'white'
                                    } 
                                }}
                            >
                                <NotificationsIcon sx={{ color: '#666666' }} />
                            </Badge>
                        </IconButton>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 1,
                        borderLeft: '1px solid #e0e0e0',
                        paddingLeft: 2
                    }}>
                        <Box>
                            <Typography 
                                variant="subtitle1" 
                                sx={{ 
                                    fontWeight: 500,
                                    color: '#333333',
                                    fontSize: '0.9rem'
                                }}
                            >
                                {user?.username || 'User'}
                            </Typography>
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    color: '#666666',
                                    fontSize: '0.75rem'
                                }}
                            >
                                {user?.role || 'Borrower'}
                            </Typography>
                        </Box>
                        <IconButton 
                            onClick={handleClick}
                            size="small"
                            aria-controls={open ? 'account-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? 'true' : undefined}
                        >
                            <AccountCircleIcon 
                                sx={{ 
                                    fontSize: 32,
                                    color: '#666666'
                                }} 
                            />
                        </IconButton>
                        <Menu
                            id="account-menu"
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            onClick={handleClose}
                            PaperProps={{
                                elevation: 0,
                                sx: {
                                    overflow: 'visible',
                                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                    mt: 1.5,
                                    '& .MuiMenuItem-root': {
                                        px: 2,
                                        py: 1,
                                        gap: 1.5,
                                    },
                                },
                            }}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        >
                            {String(user?.role).toUpperCase() !== 'SUPERADMIN' && (
                                <MenuItem onClick={handleProfile}>
                                    <ListItemIcon>
                                        <PersonIcon fontSize="small" />
                                    </ListItemIcon>
                                    Profile
                                </MenuItem>
                            )}
                            <MenuItem onClick={handleChangePassword}>
                                <ListItemIcon>
                                    <LockIcon fontSize="small" />
                                </ListItemIcon>
                                Change Password
                            </MenuItem>
                            <MenuItem onClick={handleLogoutClick}>
                                <ListItemIcon>
                                    <LogoutIcon fontSize="small" color="error" />
                                </ListItemIcon>
                                <Typography color="error">Logout</Typography>
                            </MenuItem>
                        </Menu>
                    </Box>
                </Box>
            </Toolbar>

            <LogoutDialog 
                open={logoutDialogOpen}
                onClose={handleLogoutCancel}
                onConfirm={handleLogoutConfirm}
            />
                <NotificationsPopup 
                anchorEl={notificationsAnchorEl}
                open={notificationsOpen}
                onClose={handleNotificationsClose}
            />
        </AppBar>
    );
};

export default Header;
